import {
  numberProp,
  templateCycle,
  templateSchedule,
  type WorkoutNode,
} from '@gymcloud/mixedscript'

import type { ActiveTemplate } from '~/data/templates'
import { daysIntoCycle } from '~/lib/weekdays'

/**
 * What the active run offers next, said the way somebody would say it — the same rules the
 * Android start-a-workout page's badge follows. A strict schedule puts workouts on days, so
 * it answers in days: "Today", "Tomorrow", "In 3 days" on a rest day, looking forward to
 * whichever workout the cycle reaches first. Any other schedule only knows what is next.
 * A template with one workout has nothing worth distinguishing, and says nothing.
 */
export function nextWorkoutPhrase(active: ActiveTemplate, at: number = Date.now()): string | null {
  const node = active.node
  if (node === null || node.workouts.length <= 1) {
    return null
  }
  const strict = templateSchedule(node) === 'strict'
  if (active.next !== null) {
    return strict ? `Today: ${active.next.name}` : `Next: ${active.next.name}`
  }
  if (!strict) {
    return null
  }
  // A rest day: the thing worth saying is what is coming, and how long the wait is.
  const cycle = templateCycle(node)
  if (cycle === null || cycle <= 0) {
    return null
  }
  const today = daysIntoCycle(active.enrollment.startedAt, at, active.enrollment.anchorDay ?? null)
  const coming = upcoming(node.workouts, today, cycle)
  if (coming === null) {
    return null
  }
  return coming.away === 1
    ? `Tomorrow: ${coming.workout.name}`
    : `In ${String(coming.away)} days: ${coming.workout.name}`
}

/** The workout a strict cycle reaches first from today, and how many days off it is. */
function upcoming(
  workouts: readonly WorkoutNode[],
  today: number,
  cycle: number,
): { workout: WorkoutNode; away: number } | null {
  for (let away = 0; away < cycle; away++) {
    const day = (((today + away) % cycle) + cycle) % cycle
    const workout = workouts.find((held) => numberProp(held.props, 'day') === day)
    if (workout !== undefined) {
      return { workout, away }
    }
  }
  return null
}
